'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'

const CEO_PIN = '5050'
const REFRESH_INTERVAL = 30000 // 30 seconds

interface AgentData {
  status: string
  role: string
  task: string | null
  last_heartbeat: string | null
  progress: number | null
  blocked_on: string | null
}

interface FleetData {
  timestamp: string
  cycle: number
  agents: Record<string, AgentData>
  tasks: {
    in_progress: Array<{ id: string; title: string; assigned_to: string; priority: string }>
    blocked: Array<{ id: string; title: string; blocked_reason?: string }>
    backlog: Array<{ id: string; title: string; priority: string }>
  }
  alerts: Array<{ message: string; agent?: string }>
  decisions_needed: Array<{ from: string; description: string; requested_at: string }>
}

interface CouncilMessage {
  id: string
  ts: string
  author: string
  message: string
}

interface DashboardData {
  timestamp: string
  fleet: FleetData | null
  system: {
    cpu: string
    memory: { used: string; total: string; percent: number }
    disk: { used: string; total: string; percent: number }
    uptime: string
  }
  services: {
    app: { status: string; responseMs: number }
    growthEngine: { status: string; responseMs: number }
  }
  controlCentre?: {
    approvals: Array<{ source: string; subject: string; summary: string; ts: string }>
    research: Array<{ source: string; category: string; summary: string; detail?: string; ts: string }>
    kanban: { lines: string[]; source: string }
    goals: { lines: string[]; source: string }
    currentFocus: string[]
    track: { dateUtc: string; events: Array<{ ts: string; type: string; source: string; summary: string }> }
  }
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'now'
  if (mins < 60) return `${mins}m`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h`
  return `${Math.round(hrs / 24)}d`
}

function StatusDot({ status }: { status: string }) {
  const color = {
    active: 'bg-green-500',
    idle: 'bg-gray-400',
    blocked: 'bg-red-500',
    stalled: 'bg-red-500',
    healthy: 'bg-green-500',
    degraded: 'bg-yellow-500',
    down: 'bg-red-500',
  }[status] || 'bg-gray-400'

  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${color} ${status === 'active' ? 'animate-pulse' : ''}`} />
}

function AgentCard({ name, agent }: { name: string; agent: AgentData }) {
  const emoji: Record<string, string> = {
    rivet: '🔧', builder: '🔨', susan: '💼', harper: '📊',
    sentinel: '🛡️', radar: '🔭', herald: '📢', cog: '⚙️',
  }

  const progressPercent = agent.progress !== null ? Math.max(0, Math.min(100, Math.round(agent.progress * 100))) : null

  return (
    <div className={`rounded-xl p-3.5 shadow-sm ${
      agent.status === 'blocked' || agent.status === 'stalled'
        ? 'bg-red-500/10 border border-red-500/30'
        : 'bg-white/5 border border-white/10'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{emoji[name] || '🤖'}</span>
          <span className="font-semibold text-sm capitalize">{name}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusDot status={agent.status} />
          <span className="text-[11px] text-gray-400">{timeAgo(agent.last_heartbeat)}</span>
        </div>
      </div>

      <div className="text-[11px] text-gray-400 mb-2 uppercase tracking-wide">{agent.role}</div>

      {agent.task ? (
        <div className="text-xs text-blue-200 leading-snug line-clamp-2">📋 {agent.task}</div>
      ) : (
        <div className="text-xs text-gray-500">idle</div>
      )}

      {progressPercent !== null && progressPercent > 0 && (
        <div className="mt-2">
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            <span>Progress</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
      )}

      {agent.blocked_on && (
        <div className="text-xs text-red-300 mt-2 line-clamp-2">🔴 {agent.blocked_on}</div>
      )}
    </div>
  )
}

function ServiceBadge({ name, status, ms }: { name: string; status: string; ms: number }) {
  return (
    <div className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 border border-white/10">
      <div className="flex items-center gap-2">
        <StatusDot status={status} />
        <span className="text-sm font-medium">{name}</span>
      </div>
      <span className="text-xs text-gray-400">{ms}ms</span>
    </div>
  )
}

function SystemBar({ label, percent, value }: { label: string; percent: number; value: string }) {
  const color = percent > 80 ? 'bg-red-500' : percent > 60 ? 'bg-yellow-500' : 'bg-green-500'
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className="text-gray-300">{value}</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
    </div>
  )
}

export default function CEODashboard() {
  const [data, setData] = useState<DashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())
  const [councilMessages, setCouncilMessages] = useState<CouncilMessage[]>([])
  const [councilInput, setCouncilInput] = useState('')
  const [councilLoading, setCouncilLoading] = useState(false)
  const [councilError, setCouncilError] = useState<string | null>(null)

  const fetchCouncilMessages = useCallback(async () => {
    try {
      const res = await fetch(`/api/ceo/council/messages?pin=${CEO_PIN}`, { cache: 'no-store' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const json = await res.json()
      setCouncilMessages(Array.isArray(json.messages) ? json.messages : [])
      setCouncilError(null)
    } catch (e) {
      setCouncilError(e instanceof Error ? e.message : 'Failed to load council messages')
    }
  }, [])

  const fetchData = useCallback(async () => {
    try {
      const [fleetRes] = await Promise.all([
        fetch(`/api/ceo/fleet?pin=${CEO_PIN}`),
        fetchCouncilMessages(),
      ])

      if (!fleetRes.ok) throw new Error(`HTTP ${fleetRes.status}`)
      const json = await fleetRes.json()
      setData(json)
      setError(null)
      setLastRefresh(new Date())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch')
    } finally {
      setLoading(false)
    }
  }, [fetchCouncilMessages])

  useEffect(() => {
    fetchData()
    const interval = setInterval(fetchData, REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [fetchData])

  const sendCouncilMessage = useCallback(async () => {
    const message = councilInput.trim()
    if (!message || councilLoading) return

    setCouncilLoading(true)
    try {
      const res = await fetch('/api/ceo/council/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pin: CEO_PIN,
          author: 'michael',
          message,
        }),
      })

      if (!res.ok) {
        const json = await res.json().catch(() => ({}))
        throw new Error(json?.error || `HTTP ${res.status}`)
      }

      setCouncilInput('')
      await fetchCouncilMessages()
    } catch (e) {
      setCouncilError(e instanceof Error ? e.message : 'Failed to send message')
    } finally {
      setCouncilLoading(false)
    }
  }, [councilInput, councilLoading, fetchCouncilMessages])

  const councilComposerDisabled = useMemo(() => councilLoading || councilInput.trim().length === 0, [councilInput, councilLoading])

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full" />
      </div>
    )
  }

  const fleet = data?.fleet
  const agents = fleet?.agents || {}
  const agentNames = Object.keys(agents)
  const activeCount = agentNames.filter(n => agents[n].status === 'active').length
  const blockedCount = agentNames.filter(n => agents[n].status === 'blocked' || agents[n].status === 'stalled').length
  const alerts = fleet?.alerts || []
  const decisions = fleet?.decisions_needed || []
  const cc = data?.controlCentre

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-950/95 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">RateRight HQ</h1>
            <p className="text-xs text-gray-500">
              Updated {lastRefresh.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
              {fleet && <span> · Cycle {fleet.cycle}</span>}
            </p>
          </div>
          <button
            onClick={fetchData}
            className="p-2 rounded-lg bg-white/5 active:bg-white/10 transition"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <div className="px-4 py-4 space-y-5 max-w-4xl mx-auto pb-40">
        {/* Alerts */}
        {(alerts.length > 0 || decisions.length > 0) && (
          <div className="space-y-2">
            {decisions.map((d, i) => (
              <div key={`d-${i}`} className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <span className="text-red-400 text-lg">🔴</span>
                  <div>
                    <div className="text-sm font-medium text-red-300">Decision needed</div>
                    <div className="text-xs text-gray-300 mt-0.5">{d.description}</div>
                    <div className="text-xs text-gray-500 mt-1">from {d.from} · {timeAgo(d.requested_at)}</div>
                  </div>
                </div>
              </div>
            ))}
            {alerts.map((a, i) => (
              <div key={`a-${i}`} className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-400">⚠️</span>
                  <div className="text-xs text-gray-300">{a.message}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Fleet Summary */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Fleet</h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-green-400">{activeCount} active</span>
              {blockedCount > 0 && <span className="text-red-400">{blockedCount} blocked</span>}
              <span className="text-gray-500">{agentNames.length} total</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {agentNames.map(name => (
              <AgentCard key={name} name={name} agent={agents[name]} />
            ))}
          </div>
        </div>

        {/* Active Tasks */}
        {fleet && fleet.tasks.in_progress.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Active Tasks</h2>
            <div className="space-y-1.5">
              {fleet.tasks.in_progress.map(task => (
                <div key={task.id} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm leading-snug break-words">{task.title}</div>
                    <div className="text-xs text-gray-500 mt-1">→ {task.assigned_to}</div>
                  </div>
                  <span className={`shrink-0 text-xs px-1.5 py-0.5 rounded ${
                    task.priority === 'high' ? 'bg-red-500/20 text-red-300' :
                    task.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-gray-500/20 text-gray-300'
                  }`}>{task.priority}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services */}
        {data && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Services</h2>
            <div className="space-y-1.5">
              <ServiceBadge name="RateRight App" status={data.services.app.status} ms={data.services.app.responseMs} />
              <ServiceBadge name="Growth Engine" status={data.services.growthEngine.status} ms={data.services.growthEngine.responseMs} />
            </div>
          </div>
        )}

        {/* System */}
        {data && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">System</h2>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2.5">
              <SystemBar label="CPU" percent={parseFloat(data.system.cpu)} value={data.system.cpu} />
              <SystemBar 
                label="Memory" 
                percent={data.system.memory.percent} 
                value={`${data.system.memory.used} / ${data.system.memory.total}`} 
              />
              <SystemBar 
                label="Disk" 
                percent={data.system.disk.percent} 
                value={`${data.system.disk.used} / ${data.system.disk.total}`} 
              />
              <div className="text-xs text-gray-500 pt-1">⏱ {data.system.uptime}</div>
            </div>
          </div>
        )}

        {/* Michael Action Queue */}
        {cc && cc.approvals.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Needs Michael Action</h2>
            <div className="space-y-2">
              {cc.approvals.slice(0, 8).map((a, i) => (
                <div key={`approval-${i}`} className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                  <div className="text-xs text-red-300 font-medium">{a.subject}</div>
                  <div className="text-xs text-gray-300 mt-1">{a.summary}</div>
                  <div className="text-xs text-gray-500 mt-1">{a.source} · {timeAgo(a.ts)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Research */}
        {cc && cc.research.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Research Feed</h2>
            <div className="space-y-1.5">
              {cc.research.slice(0, 8).map((r, i) => (
                <div key={`research-${i}`} className="bg-white/5 border border-white/10 rounded-lg p-2.5">
                  <div className="text-xs text-blue-300">{r.source} · {r.category}</div>
                  <div className="text-sm text-gray-200">{r.summary}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Kanban + Goals */}
        {cc && (
          <div className="grid grid-cols-1 gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Kanban Snapshot</h2>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-1">
                {cc.kanban.lines.slice(0, 6).map((line, i) => (
                  <div key={`kanban-${i}`} className="text-xs text-gray-300 font-mono truncate">{line}</div>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Goals Snapshot</h2>
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-1">
                {cc.goals.lines.slice(0, 6).map((line, i) => (
                  <div key={`goal-${i}`} className="text-xs text-gray-300 font-mono truncate">{line}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Daily Track */}
        {cc && (
          <div>
            <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Daily Track</h2>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-1.5">
              {cc.track.events.slice(0, 12).map((e, i) => (
                <div key={`track-${i}`} className="text-xs">
                  <span className="text-gray-500">{new Date(e.ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</span>
                  <span className="text-gray-400"> · {e.source} · </span>
                  <span className="text-gray-200">{e.summary}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Council */}
        <div>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-2">Council</h2>
          <div className="bg-white/5 border border-white/10 rounded-xl p-3 max-h-72 overflow-y-auto space-y-2">
            {councilMessages.length === 0 ? (
              <div className="text-xs text-gray-500">No council messages yet.</div>
            ) : (
              councilMessages.slice(-50).map((m) => (
                <div key={m.id} className="text-xs border border-white/10 rounded-lg p-2 bg-gray-900/40">
                  <div className="text-gray-400">{m.author} · {new Date(m.ts).toLocaleString('en-AU')}</div>
                  <div className="text-gray-200 mt-1 whitespace-pre-wrap break-words">{m.message}</div>
                </div>
              ))
            )}
          </div>
          {councilError && <div className="text-xs text-red-300 mt-2">{councilError}</div>}
        </div>

        {/* Error banner */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-center">
            <div className="text-sm text-red-300">Failed to refresh</div>
            <div className="text-xs text-gray-500 mt-1">{error}</div>
          </div>
        )}
      </div>

      {/* Sticky council composer */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-gray-950/95 backdrop-blur px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] pt-3">
        <div className="max-w-4xl mx-auto flex gap-2 items-end">
          <textarea
            value={councilInput}
            onChange={(e) => setCouncilInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault()
                void sendCouncilMessage()
              }
            }}
            placeholder="Type to council… (Cmd/Ctrl+Enter to send)"
            className="min-h-[44px] max-h-36 w-full resize-y rounded-md border border-white/15 bg-black/30 px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={() => void sendCouncilMessage()}
            disabled={councilComposerDisabled}
            className="h-11 px-4 rounded-md bg-blue-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {councilLoading ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}
